import { Component, OnInit, OnDestroy } from '@angular/core';
import { RfqDetailsComponent } from './steps/rfq-details/rfq-details.component';
import { AddPartsComponent } from './steps/add-parts/add-parts.component';
import { ReviewSummaryComponent } from './steps/review-summary/review-summary.component';
import { SubmitComponent } from './steps/submit/submit.component';
import { NgIf } from '@angular/common';
import { IndexedDBService } from '../../services/indexed.db.service';
import { HostListener } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { RfqIntakeService } from '../../services/rfq.intake.service';
import { ToastrService } from 'ngx-toastr';
import { Subscription } from 'rxjs';
// Add this before the component class
interface RfqFormData {
  rfqName: string;
  fullName: string;
  companyName: string;
  email: string;
  phoneNo: string;
  quoteBy: Date;
  deliverBy: Date;
  address1: string;
  address2?: string;
  country: string;
  countryId: string;
  state: string;
  stateId: string;
  city: string;
  zip: string;
  certificates: string[];
  industry: string[];
  inspection: string[];
  paymentTerms: string[];
}

interface RfqStepData {
  formData: RfqFormData;
  showSummary: boolean;
  hasUploadedFiles: boolean;
  isSubmitted?: boolean;
  backToRfqParts?: boolean;
}

@Component({
  selector: 'app-rfq-intake',
  imports: [NgIf,RfqDetailsComponent,AddPartsComponent,ReviewSummaryComponent,SubmitComponent],
  templateUrl: './rfq-intake.component.html',
  styleUrl: './rfq-intake.component.scss'
})
export class RfqIntakeComponent implements OnInit, OnDestroy {
  currentStep = 1;
  rfqData: any = {}; // Shared data object
  step1Completed = false;
  step3Enabled = false; // Add this flag to control step 3's enabled state
  dbCleared = false;
  step4Enabled = false; // Add this flag to control step 4's enabled state
  isSubmitted = false; // Add this flag to track submission status
  companyGuid: string = '';
  isLoadingSupplierDetails: boolean = false;
  supplierCompanyName: string | null = null;
  supplierLogoUrl: string | null = null;
  isInIframe: boolean = false;
  private subscriptions: Subscription[] = [];

  constructor(
    private indexedDBService: IndexedDBService,
    private route: ActivatedRoute,
    private rfqIntakeService: RfqIntakeService,
    private toastr: ToastrService,
    private router: Router
  ) {}

  @HostListener('window:beforeunload', ['$event'])
  unloadNotification($event: any): void {
    this.indexedDBService.clearRfqIntakeData();
  }

  ngOnInit(){
    this.detectIframe();
    
    const queryParamsSub = this.route.queryParams.subscribe(params => {
      const duid = params['duid'];
      if (!duid || ["undefined","null"].indexOf(duid) >= 0) {
        this.toastr.error('Please contact support team to get your DUID');
        return;
      }
      this.companyGuid = duid;
      this.rfqIntakeService.setCompanyGuid(duid);
      this.validateCompanyGuid(duid);
    });
    this.subscriptions.push(queryParamsSub); 
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(sub => sub.unsubscribe());
  }

  private detectIframe(): void {
    try {
      this.isInIframe = window.self !== window.top;
    } catch (error) {
      this.isInIframe = true;
    }
  }

  /**
   * Validates company GUID by calling the supplier details API
   * @param companyGuid The company GUID to validate
   */
  private validateCompanyGuid(companyGuid: string): void {
    this.isLoadingSupplierDetails = true;
    
    const supplierDetailsSub = this.rfqIntakeService.getSupplierDetailsByCompanyGuid(companyGuid)
      .subscribe({
        next: (response) => {
          this.isLoadingSupplierDetails = false;
          
          if (!response.data) {
            // Invalid GUID - navigate to 404 page
            this.router.navigate(['/404']);
            return;
          }

          // Parse metadata to extract company name
          const metadata = this.rfqIntakeService.parseSupplierMetadata(response.data.metaData);
          
          if (metadata) {
            this.supplierCompanyName = metadata.company_name || null;
            this.supplierLogoUrl = metadata.company_url || null;
          } else {
            this.toastr.warning('Company details found but company name is missing.');
          }
        },
        error: (error) => {
          this.isLoadingSupplierDetails = false;
          // Handle API errors
          const errorMessage = error?.error?.message || 'Unable to validate company details. Please try again.';
          this.toastr.error(errorMessage);
        }
      });
    
    this.subscriptions.push(supplierDetailsSub);
  }


  goToStep(step: number) {
    // If RFQ is submitted, don't allow navigation to any step
    if (this.isSubmitted) return;
    
    // Only allow navigation to step 2 if step 1 is completed
    if (step === 2 && !this.step1Completed) return;
    // Only allow navigation to step 3 if it's enabled
    if (step === 3 && !this.step3Enabled) return;
    // Only allow navigation to step 4 if it's enabled
    if (step === 4 && !this.step4Enabled) return;
    this.currentStep = step;
  }

  // Add method to enable step 3 and navigate to it
  addPartsDataReceiver(data: any) {
    if(data && data.backToRfqDetails) {
      this.currentStep=1;
      return;
    }
    this.step3Enabled = true;
    this.currentStep = 3;
  }

  enableAndGoToStep4() {
    this.step4Enabled = true;
    this.currentStep = 4;
  }

  nextStep(data: RfqStepData) {
    if(data && data.backToRfqParts) {
      this.rfqData={};
      this.currentStep=2;
      return;
    }
    // Check if this is a submission response
    if (data.isSubmitted) {
      this.isSubmitted = true;
      return;
    }
    
    // Extract formData and flags
    const { formData, showSummary, hasUploadedFiles } = data;
    
    // Update rfqData with the correct structure
    this.rfqData = {
      ...this.rfqData,
      ...formData,  // Spread the form data
      showSummary,  // Add the flags
      hasUploadedFiles
    };
    
    if (this.currentStep === 1) {
      this.step1Completed = true;
    }
    
    this.currentStep++;
  }

  /**
   * Moves to the previous step
   * @param data Optional data from the current step that should be saved
   */
  prevStep(data?: any) {
    this.rfqData={};
      // Coming from goBackToParts in review-summary (step 3)
      if (data && data.editSelectedPart) {
        this.rfqData = {
          selectedPartForEdit: data.rfqPartId, 
        };
        this.currentStep--;
        return;
      }

    // Handle back navigation based on current step
    if (this.currentStep === 3) {
      // Back to Add Parts from Review Summary
      this.rfqData = { 
        ...this.rfqData, 
        showSummary: true, 
        hasUploadedFiles: true 
      };
      this.currentStep--;
    } 
     
    if (this.currentStep === 2) {
      // Back to RFQ Details from Add Parts
      // Check if parts exist in IndexedDB
      const indexDBSub = this.indexedDBService.getRfqIntakeData('temp-rfq').subscribe({
        next: (record: any) => {
          const hasPartDetails = record?.partDetails && record.partDetails.length > 0;
          this.rfqData = { 
            ...this.rfqData, 
            showSummary: hasPartDetails, 
            hasUploadedFiles: hasPartDetails 
          };
          this.currentStep--;
        },
        error: (err) => {
          // Default to false if error occurs
          this.rfqData = { 
            ...this.rfqData, 
            showSummary: false, 
            hasUploadedFiles: false 
          };
          this.toastr.error('Unable to load part details. Please try again.');
        }
      });
      this.subscriptions.push(indexDBSub);
    }
  }

  isStepCompleted(step: number): boolean {
    return this.currentStep - 1 === step;
  }
}
